import React, { useState, useEffect, useCallback } from 'react';
import { MenuItem, TextField, CircularProgress } from '@mui/material';
import { fetchEmployees } from '../../services/employeeApi';

/**
 * AssignUserSelect Component
 * Dropdown to select/assign an employee/user
 * Fetches employees from API
 * 
 * @param {string} value - Selected employee_id value
 * @param {function} onChange - Callback when selection changes
 * @param {array} employees - Optional pre-loaded employees array (for optimization)
 */
const AssignUserSelect = ({ value, onChange, employees: propEmployees = null }) => {
  const [employees, setEmployees] = useState(propEmployees || []);
  const [loading, setLoading] = useState(!propEmployees);

  const loadEmployees = useCallback(async () => {
    if (propEmployees) {
      setEmployees(propEmployees);
      return;
    }

    setLoading(true);
    try {
      const response = await fetchEmployees();
      if (response.success && Array.isArray(response.data)) {
        // Filter only active employees
        const activeEmployees = response.data.filter(
          emp => emp.employee_status === 'Active' || !emp.employee_status
        );
        setEmployees(activeEmployees);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [propEmployees]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const getEmployeeDisplayName = (employee) => {
    if (employee.full_name) return `${employee.full_name} (${employee.employee_id})`;
    if (employee.user_name) return `${employee.user_name} (${employee.employee_id})`;
    return employee.employee_id || 'Unknown';
  };

  return (
    <TextField
      select
      label="Assignee"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      size="small"
      disabled={loading}
      sx={{ mb: 2 }}
      InputProps={{
        endAdornment: loading ? <CircularProgress size={20} /> : null,
      }}
    >
      {loading ? (
        <MenuItem disabled>Loading employees...</MenuItem>
      ) : employees.length === 0 ? (
        <MenuItem disabled>No employees found</MenuItem>
      ) : (
        employees.map((employee) => (
          <MenuItem key={employee._id || employee.employee_id} value={employee.employee_id}>
            {getEmployeeDisplayName(employee)}
          </MenuItem>
        ))
      )}
    </TextField>
  );
};

export default AssignUserSelect;

